/**
 * The second of the three sheets the public screens carry: a 1:100 ground-floor plan of a
 * four-storey residential block — `PÔDORYS 1.NP`, drawn the way a Slovak DSP set draws one.
 *
 * **What the sheet is.** A 20 × 14 m block on a grid of axes A–F and 1–5, external walls in
 * 600 mm poché, a staircase and lift core on axes C–D, three flats around it, and the
 * apparatus that makes a drawing a document rather than a picture: dimension chains, axis
 * bubbles, a north point, a scale bar, a key, a version table, a flat schedule and a title
 * block. Two marks on it are the product rather than the building — the `r04` version cloud
 * and the comment pin — and they are the only two coloured things here.
 *
 * **Every coordinate is computed once, at module scope.** The ~700 elements come out of the
 * helpers below (a window, a door leaf and its swing, a dimension chain, a sanitary fitting)
 * rather than being pasted as literals, exactly as in `AxonometryDrawing`: the arithmetic is
 * deterministic, there is nothing to recompute per render and nothing random, so the same
 * sheet is drawn on every reload. The JSX at the bottom maps over the finished arrays, and
 * the order of this file is the order of the sheet.
 *
 * **The annotations are catalog messages**, not literals baked into the geometry — the
 * landing page has an EN/SK/DE switcher in its own footer, and a plan labelled `KUCHYŇA` on
 * an English page is the one element of the screen that did not get translated. Numbers do
 * not go through the catalog: `lib/i18n/formats.ts` has no number helper, and a drawing keeps
 * the continental spelling (`24,6 m²`, `1:100`) in every language it is printed in. Neither
 * do identifiers — `RES-B-L01-A201`, `r04`, `SO 01`, `DSP`, the axis letters — because they
 * are the same string in every language by definition.
 *
 * **Every colour is a token applied through the stylesheet** (`scripts/guard.mjs` fails the
 * build on a hex anywhere in `src/`), and the sheet paints no ground of its own: see the
 * `ground` prop and the stylesheet header.
 *
 * `aria-hidden`: it is the picture on the wall, not information. A screen reader reading out
 * a room schedule nobody can act on would be reading the wallpaper.
 */

import { useId } from 'react';

import { useMessages } from '@/lib/i18n';
import type { MessageKeys } from '@/lib/i18n';

import styles from './PlanSheetDrawing.module.css';

/* ------------------------------------------------------------------ line families */

/**
 * The stroke, fill and type families of the sheet, named rather than numbered. A drawing has
 * a small fixed set of line weights and everything is drawn in one of them; the stylesheet
 * holds the weights and the tokens, this holds the vocabulary.
 */
type Ink =
  | 'paperEdge'
  | 'sec'
  | 'frame'
  | 'blank'
  | 'loggia'
  | 'wallBg'
  | 'wallHatch'
  | 'wallO'
  | 'wallO2'
  | 'wl'
  | 'part'
  | 'win'
  | 'glass'
  | 'leaf'
  | 'swing'
  | 'fix'
  | 'fixd'
  | 'cut'
  | 'arrow'
  | 'axis'
  | 'bub'
  | 'dim'
  | 'dimx'
  | 'tick'
  | 'rev'
  | 'pin'
  | 'pinl'
  | 'north'
  | 'sbFilled'
  | 'sbOpen'
  | 'tbBox'
  | 'tbLine'
  | 'tbRule'
  | 'roomName'
  | 'roomArea'
  | 'flatNo'
  | 'bubText'
  | 'dimText'
  | 'revText'
  | 'pinText'
  | 'northText'
  | 'sbText'
  | 'sbLabel'
  | 'tbHead'
  | 'tbKey'
  | 'tbBody'
  | 'tbFoot'
  | 'tbValue'
  | 'tbValueSmall'
  | 'wordmark';

const CLASSES: Readonly<Record<Ink, string | undefined>> = {
  paperEdge: styles.paperEdge,
  sec: styles.sec,
  frame: styles.frame,
  blank: styles.blank,
  loggia: styles.loggia,
  wallBg: styles.wallBg,
  wallHatch: styles.wallHatch,
  wallO: styles.wallO,
  wallO2: styles.wallO2,
  wl: styles.wl,
  part: styles.part,
  win: styles.win,
  glass: styles.glass,
  leaf: styles.leaf,
  swing: styles.swing,
  fix: styles.fix,
  fixd: styles.fixd,
  cut: styles.cut,
  arrow: styles.arrow,
  axis: styles.axis,
  bub: styles.bub,
  dim: styles.dim,
  dimx: styles.dimx,
  tick: styles.tick,
  rev: styles.rev,
  pin: styles.pin,
  pinl: styles.pinl,
  north: styles.north,
  sbFilled: styles.sbFilled,
  sbOpen: styles.sbOpen,
  tbBox: styles.tbBox,
  tbLine: styles.tbLine,
  tbRule: styles.tbRule,
  roomName: styles.roomName,
  roomArea: styles.roomArea,
  flatNo: styles.flatNo,
  bubText: styles.bubText,
  dimText: styles.dimText,
  revText: styles.revText,
  pinText: styles.pinText,
  northText: styles.northText,
  sbText: styles.sbText,
  sbLabel: styles.sbLabel,
  tbHead: styles.tbHead,
  tbKey: styles.tbKey,
  tbBody: styles.tbBody,
  tbFoot: styles.tbFoot,
  tbValue: styles.tbValue,
  tbValueSmall: styles.tbValueSmall,
  wordmark: styles.wordmark,
};

const ink = (...names: readonly Ink[]) =>
  names
    .map((name) => CLASSES[name])
    .filter((name): name is string => name !== undefined)
    .join(' ');

/* ------------------------------------------------------------------ the shape list */

type Message = MessageKeys['drawing'];

type Shape =
  | {
      readonly kind: 'rect';
      readonly cls: string;
      readonly x: number;
      readonly y: number;
      readonly w: number;
      readonly h: number;
      readonly rx: number;
      /** `hatch` fills this rect with the poché pattern, which is scoped per instance. */
      readonly hatched: boolean;
    }
  /** The front sheet: its edge treatment depends on what it is lying on. See `ground`. */
  | {
      readonly kind: 'sheet';
      readonly x: number;
      readonly y: number;
      readonly w: number;
      readonly h: number;
    }
  | {
      readonly kind: 'line';
      readonly cls: string;
      readonly x1: number;
      readonly y1: number;
      readonly x2: number;
      readonly y2: number;
    }
  | {
      readonly kind: 'path';
      readonly cls: string;
      readonly d: string;
      readonly evenOdd: boolean;
      readonly hatched: boolean;
    }
  | {
      readonly kind: 'circle';
      readonly cls: string;
      readonly cx: number;
      readonly cy: number;
      readonly r: number;
    }
  | {
      readonly kind: 'ellipse';
      readonly cls: string;
      readonly cx: number;
      readonly cy: number;
      readonly rx: number;
      readonly ry: number;
    }
  /** A number, an identifier or a symbol: never translated, see the module header. */
  | {
      readonly kind: 'glyph';
      readonly cls: string;
      readonly x: number;
      readonly y: number;
      readonly text: string;
      readonly rotate: boolean;
    }
  | {
      readonly kind: 'label';
      readonly cls: string;
      readonly x: number;
      readonly y: number;
      readonly message: Message;
      readonly rotate: boolean;
    };

let current: Shape[] = [];

/** Closes the section being drawn and starts the next one. Order on the sheet is order here. */
function section(): readonly Shape[] {
  const done = current;
  current = [];
  return done;
}

function rect(x: number, y: number, w: number, h: number, cls: string, rx = 0) {
  current.push({ kind: 'rect', cls, x, y, w, h, rx, hatched: false });
}

/** A rect filled with the 45° section hatch — always drawn over a white one, never alone. */
function hatchRect(x: number, y: number, w: number, h: number) {
  current.push({ kind: 'rect', cls: ink('wallHatch'), x, y, w, h, rx: 0, hatched: true });
}

function line(x1: number, y1: number, x2: number, y2: number, cls: string) {
  current.push({ kind: 'line', cls, x1, y1, x2, y2 });
}

function path(d: string, cls: string, evenOdd = false, hatched = false) {
  current.push({ kind: 'path', cls, d, evenOdd, hatched });
}

function circle(cx: number, cy: number, r: number, cls: string) {
  current.push({ kind: 'circle', cls, cx, cy, r });
}

function ellipse(cx: number, cy: number, rx: number, ry: number, cls: string) {
  current.push({ kind: 'ellipse', cls, cx, cy, rx, ry });
}

function glyph(x: number, y: number, text: string, cls: string, rotate = false) {
  current.push({ kind: 'glyph', cls, x, y, text, rotate });
}

function label(x: number, y: number, message: Message, cls: string, rotate = false) {
  current.push({ kind: 'label', cls, x, y, message, rotate });
}

/**
 * A wall band: the outer rectangle with the inner one punched out of it, as one even-odd
 * path. Drawn twice — once white, once hatched — so the hatching can never leave the wall.
 */
function ringPath(
  ox: number,
  oy: number,
  ow: number,
  oh: number,
  ix: number,
  iy: number,
  iw: number,
  ih: number,
) {
  const d =
    `M${String(ox)},${String(oy)} H${String(ox + ow)} V${String(oy + oh)} H${String(ox)} Z ` +
    `M${String(ix)},${String(iy)} H${String(ix + iw)} V${String(iy + ih)} H${String(ix)} Z`;
  path(d, ink('wallBg'), true, false);
  path(d, ink('wallHatch'), true, true);
}

/* ------------------------------------------------------------------ the module */

/** Axis A–F, in sheet units. 1 unit = 100 mm, so the bays are 4000/3600/4400/3600/3600 mm. */
const AXES_X: readonly (readonly [string, number])[] = [
  ['A', 56],
  ['B', 96],
  ['C', 132],
  ['D', 176],
  ['E', 212],
  ['F', 248],
];
/** Axis 1–5: 3600/3600/3600/2800 mm. */
const AXES_Y: readonly (readonly [string, number])[] = [
  ['1', 68],
  ['2', 104],
  ['3', 140],
  ['4', 176],
  ['5', 204],
];

/** Wall thicknesses: external 600, staircase core 400, partition 250. */
const CORE = 4;
const PART = 2.5;

/** The outer and inner faces of the external envelope. */
const OX0 = 53;
const OY0 = 65;
const OX1 = 251;
const OY1 = 207;
const IX0 = 59;
const IY0 = 71;
const IX1 = 245;
const IY1 = 201;

/* ------------------------------------------------------------------ openings */

/**
 * A window in a wall running east–west: the band whited out, the cut wall line carried across
 * the head and the sill, the frame drawn inside the reveal and the glazing line between.
 */
function windowH(y0: number, y1: number, x0: number, x1: number) {
  const t = y1 - y0;
  rect(x0, y0, x1 - x0, t, ink('blank'));
  line(x0, y0, x1, y0, ink('wl'));
  line(x0, y1, x1, y1, ink('wl'));
  line(x0, y0, x0, y1, ink('win'));
  line(x1, y0, x1, y1, ink('win'));
  line(x0, y0 + t * 0.26, x1, y0 + t * 0.26, ink('win'));
  line(x0, y1 - t * 0.26, x1, y1 - t * 0.26, ink('win'));
  line(x0, (y0 + y1) / 2, x1, (y0 + y1) / 2, ink('glass'));
}

/** The same window in a wall running north–south. */
function windowV(x0: number, x1: number, y0: number, y1: number) {
  const t = x1 - x0;
  rect(x0, y0, t, y1 - y0, ink('blank'));
  line(x0, y0, x0, y1, ink('wl'));
  line(x1, y0, x1, y1, ink('wl'));
  line(x0, y0, x1, y0, ink('win'));
  line(x0, y1, x1, y1, ink('win'));
  line(x0 + t * 0.26, y0, x0 + t * 0.26, y1, ink('win'));
  line(x1 - t * 0.26, y0, x1 - t * 0.26, y1, ink('win'));
  line((x0 + x1) / 2, y0, (x0 + x1) / 2, y1, ink('glass'));
}

/** The leaf and its quarter-circle swing — the one symbol that says which way a door opens. */
function leaf(hx: number, hy: number, nx: number, ny: number, dx: number, dy: number, w: number) {
  const tipX = hx + nx * w;
  const tipY = hy + ny * w;
  const endX = hx + dx * w;
  const endY = hy + dy * w;
  const sweep = nx * dy - ny * dx > 0 ? 1 : 0;
  line(hx, hy, tipX, tipY, ink('leaf'));
  path(
    `M${String(tipX)},${String(tipY)} A${String(w)},${String(w)} 0 0 ${String(sweep)} ` +
      `${String(endX)},${String(endY)}`,
    ink('swing'),
  );
}

type Hinge = 'left' | 'right';

/** A door in an east–west wall. `swing` is the side of the wall the leaf opens onto. */
function doorH(y0: number, y1: number, x0: number, w: number, hinge: Hinge, swing: 'up' | 'down') {
  rect(x0, y0, w, y1 - y0, ink('blank'));
  line(x0, y0, x0, y1, ink('win'));
  line(x0 + w, y0, x0 + w, y1, ink('win'));
  const hy = swing === 'up' ? y0 : y1;
  const hx = hinge === 'left' ? x0 : x0 + w;
  const ny = swing === 'up' ? -1 : 1;
  const dx = hinge === 'left' ? 1 : -1;
  leaf(hx, hy, 0, ny, dx, 0, w);
}

/** A door in a north–south wall. `hinge` here is the top or bottom jamb. */
function doorV(
  x0: number,
  x1: number,
  y0: number,
  w: number,
  hinge: 'top' | 'bottom',
  swing: 'left' | 'right',
) {
  rect(x0, y0, x1 - x0, w, ink('blank'));
  line(x0, y0, x1, y0, ink('win'));
  line(x0, y0 + w, x1, y0 + w, ink('win'));
  const hx = swing === 'left' ? x0 : x1;
  const hy = hinge === 'top' ? y0 : y0 + w;
  const nx = swing === 'left' ? -1 : 1;
  const dy = hinge === 'top' ? 1 : -1;
  leaf(hx, hy, nx, 0, 0, dy, w);
}

/* ------------------------------------------------------------------ dimension chains */

/** Millimetres, rounded to whole, the way a chain is annotated. */
const mm = (units: number) => String(Math.round(units * 100));

/** A horizontal chain: the run, an oblique tick at every station, the figure between them. */
function chainH(y: number, stations: readonly number[]) {
  const first = stations[0];
  const last = stations[stations.length - 1];
  if (first === undefined || last === undefined) return;
  line(first, y, last, y, ink('dim'));
  for (const p of stations) line(p - 1.7, y + 1.7, p + 1.7, y - 1.7, ink('tick'));
  for (let i = 0; i < stations.length - 1; i += 1) {
    const a = stations[i];
    const b = stations[i + 1];
    if (a === undefined || b === undefined) continue;
    // A figure narrower than its own gap is a figure sitting on the ticks either side of it.
    if (b - a < 5.5) continue;
    glyph((a + b) / 2, y - 1.7, mm(b - a), ink('dimText'));
  }
}

/** The same chain turned on its side; the figures turn with it, as they do on a sheet. */
function chainV(x: number, stations: readonly number[]) {
  const first = stations[0];
  const last = stations[stations.length - 1];
  if (first === undefined || last === undefined) return;
  line(x, first, x, last, ink('dim'));
  for (const p of stations) line(x - 1.7, p + 1.7, x + 1.7, p - 1.7, ink('tick'));
  for (let i = 0; i < stations.length - 1; i += 1) {
    const a = stations[i];
    const b = stations[i + 1];
    if (a === undefined || b === undefined) continue;
    if (b - a < 5.5) continue;
    glyph(x - 1.7, (a + b) / 2, mm(b - a), ink('dimText'), true);
  }
}

/* ------------------------------------------------------------------ the sheet under it */

// The second sheet, peeking out behind the top-left corner. It is why this reads as one sheet
// out of a set rather than as a picture of a building.
rect(8, 6, 376, 282, ink('paperEdge'));
line(11.4, 8, 11.4, 286, ink('sec'));
for (const [ya, yb] of [
  [8.8, 9.9],
  [12.6, 13.7],
] as const) {
  line(20, ya, 300, ya, ink('sec'));
  line(20, yb, 300, yb, ink('sec'));
}
for (const x of [20, 34, 62, 90, 118, 146, 174, 202, 230]) line(x, 7.4, x, 15.4, ink('sec'));
const BEHIND = section();

// The front sheet: the 1.NP plan. Its own frame, and the rule that splits drawing from band.
current.push({ kind: 'sheet', x: 18, y: 16, w: 374, h: 278 });
rect(24, 22, 362, 266, ink('frame'));
line(266, 22, 266, 288, ink('frame'));
const SHEET = section();

/* ------------------------------------------------------------------ the grid */

for (const [, x] of AXES_X) line(x, 33, x, 239, ink('axis'));
for (const [, y] of AXES_Y) line(31, y, 259, y, ink('axis'));
for (const [name, x] of AXES_X) {
  for (const y of [33, 239]) {
    circle(x, y, 4.4, ink('bub'));
    glyph(x, y + 1.5, name, ink('bubText'));
  }
}
for (const [name, y] of AXES_Y) {
  for (const x of [29, 259]) {
    circle(x, y, 4.4, ink('bub'));
    glyph(x, y + 1.5, name, ink('bubText'));
  }
}
const GRID = section();

/* ------------------------------------------------------------------ the walls */

// The external envelope: 600 mm poché, hatched on an even-odd ring so it stays inside the wall.
ringPath(OX0, OY0, OX1 - OX0, OY1 - OY0, IX0, IY0, IX1 - IX0, IY1 - IY0);
rect(OX0, OY0, OX1 - OX0, OY1 - OY0, ink('wallO'));
rect(IX0, IY0, IX1 - IX0, IY1 - IY0, ink('wallO'));

// The loggia: a slab projecting south of flat 1, with a 300 mm parapet round three sides.
const LX0 = 60;
const LY0 = 207;
const LX1 = 100;
const LY1 = 226;
rect(63, 204, 34, 19, ink('loggia'));
ringPath(LX0, LY0, LX1 - LX0, LY1 - LY0, 63, LY0, 34, LY1 - 3 - LY0);
path(`M${String(LX0)},${String(LY0)} V${String(LY1)} H${String(LX1)} V${String(LY0)}`, ink('wallO2'));
path(`M63,${String(LY0)} V${String(LY1 - 3)} H97 V${String(LY0)}`, ink('wallO2'));

// The staircase core: 400 mm walls on axes C and D, closed top and bottom.
for (const cx of [132, 176]) {
  rect(cx - CORE / 2, IY0, CORE, IY1 - IY0, ink('wallBg'));
  hatchRect(cx - CORE / 2, IY0, CORE, IY1 - IY0);
  rect(cx - CORE / 2, IY0, CORE, IY1 - IY0, ink('wallO'));
}
for (const [cy, cx0, cx1] of [
  [94.5, 134, 174],
  [161.5, 134, 174],
] as const) {
  rect(cx0, cy - 1.5, cx1 - cx0, 3, ink('wallBg'));
  hatchRect(cx0, cy - 1.5, cx1 - cx0, 3);
  rect(cx0, cy - 1.5, cx1 - cx0, 3, ink('wallO'));
}

// Internal partitions: 250 mm, white poché, one thin line — a partition is not a cut wall.
const PARTITIONS: readonly (readonly [number, 'v' | 'h', number, number])[] = [
  [88, 'v', 71, 108],
  [116, 'v', 106.75, 201],
  [99, 'v', 159.75, 201],
  [108, 'h', 59, 117.25],
  [161, 'h', 59, 117.25],
  [185.25, 'h', 99, 116],
  [208, 'v', 71, 201],
  [108, 'h', 206.75, 245],
  [172, 'h', 206.75, 245],
  [187.25, 'h', 134, 174],
];
for (const [c, axis, s, e] of PARTITIONS) {
  if (axis === 'v') rect(c - PART / 2, s, PART, e - s, ink('part'));
  else rect(s, c - PART / 2, e - s, PART, ink('part'));
}
rect(178, 138.75, 67, PART, ink('part')); // axis 3 party wall, between flats 2 and 3
rect(178, 159.75, 28.75, PART, ink('part')); // flat 3 hall
const WALLS = section();

/* ------------------------------------------------------------------ the openings */

for (const [x0, x1] of [
  [62, 80],
  [98, 120],
  [182, 202],
  [218, 238],
] as const) {
  windowH(OY0, IY0, x0, x1); // north wall
}
for (const [x0, x1] of [
  [182, 200],
  [216, 234],
] as const) {
  windowH(IY1, OY1, x0, x1); // south wall
}
for (const [y0, y1] of [
  [76, 94],
  [112, 132],
  [138, 156],
  [168, 190],
] as const) {
  windowV(OX0, IX0, y0, y1); // west wall
}
for (const [y0, y1] of [
  [78, 98],
  [112, 132],
  [146, 166],
  [178, 196],
] as const) {
  windowV(IX1, OX1, y0, y1); // east wall
}
const WINDOWS = section();

doorH(IY1, OY1, 68, 16, 'left', 'up'); // balcony door to the loggia
doorH(IY1, OY1, 145, 18, 'left', 'up'); // main entrance
doorH(106.75, 109.25, 63, 8, 'left', 'up'); // kitchen to living room
doorH(106.75, 109.25, 120, 8, 'left', 'down'); // bedroom to hall
doorH(159.75, 162.25, 105, 8, 'left', 'down'); // hall to bathroom
doorH(159.75, 162.25, 64, 8, 'left', 'up'); // living room to second bedroom
doorH(184, 186.5, 104, 7, 'left', 'down'); // bathroom to wc
doorV(114.75, 117.25, 124, 8, 'top', 'left'); // hall to living room
doorV(130, 134, 118, 8, 'top', 'left'); // core to flat 1
doorV(174, 178, 98, 8, 'top', 'right'); // core to flat 2
doorV(174, 178, 166, 8, 'top', 'right'); // core to flat 3
doorV(206.75, 209.25, 76, 8, 'top', 'right'); // flat 2, living room to bedroom
doorH(106.75, 109.25, 224, 8, 'left', 'down'); // flat 2 bathroom
doorV(206.75, 209.25, 146, 8, 'top', 'right'); // flat 3, living room to bedroom
doorH(170.75, 173.25, 228, 8, 'left', 'down'); // flat 3 bathroom
doorH(186, 188.5, 150, 10, 'left', 'up'); // lobby to the core hall
const DOORS = section();

/* ------------------------------------------------------------------ the core */

// The lift: the shaft, its diagonal cross, and the landing door.
line(134, 71, 174, 93, ink('fix'));
line(174, 71, 134, 93, ink('fix'));
rect(134, 71, 40, 22, ink('fix'));
doorH(93, 96, 148, 12, 'left', 'down');

// The dog-leg stair: the landing edge, eleven treads either side of the well, the cut line
// through the flight and the arrow that says which way is up.
const STAIR_END = 160;
line(134, 110, 174, 110, ink('fix'));
const TREADS = 12;
for (let i = 1; i < TREADS; i += 1) {
  const y = 110 + ((STAIR_END - 110) * i) / TREADS;
  line(134, y, 152.6, y, ink('fix'));
  line(155.4, y, 174, y, ink('fix'));
}
line(152.6, 110, 152.6, STAIR_END, ink('fix'));
line(155.4, 110, 155.4, STAIR_END, ink('fix'));
path('M131,142 L142,136 L139,131 L156,122', ink('cut'));
path('M164.7,157 V116 M162.3,120.5 L164.7,115 L167.1,120.5', ink('arrow'));
circle(143.3, 157, 1.1, ink('fixd'));
const CORE_EQUIPMENT = section();

/* ------------------------------------------------------------------ the fittings */

/** A bath: the tub, its inner line and the waste. Line work only, no fill. */
function bath(x: number, y: number, w: number, h: number) {
  rect(x, y, w, h, ink('fix'));
  rect(x + 0.9, y + 0.9, w - 1.8, h - 1.8, ink('fixd'), 1.3);
  circle(x + w - 3, y + h / 2, 0.8, ink('fixd'));
}

/** A wc: the cistern and the pan, which is the drafting symbol and nothing more. */
function wc(x: number, y: number) {
  rect(x, y, 5.2, 1.8, ink('fix'));
  ellipse(x + 2.6, y + 4.6, 2.4, 3.1, ink('fix'));
}

function basin(x: number, y: number, w: number, h: number) {
  rect(x, y, w, h, ink('fix'), 1.2);
  circle(x + w / 2, y + h * 0.55, 0.7, ink('fixd'));
}

/** A kitchen run: the worktop, the sink at one end and the four hobs at the other. */
function kitchenRun(x: number, y: number, w: number, h: number, horizontal: boolean) {
  rect(x, y, w, h, ink('fix'));
  if (horizontal) {
    rect(x + 2, y + 0.9, 7, h - 1.8, ink('fixd'));
    for (let i = 0; i < 2; i += 1) {
      for (let j = 0; j < 2; j += 1) {
        circle(x + w - 7.5 + i * 4, y + h * 0.3 + j * h * 0.4, 0.85, ink('fixd'));
      }
    }
  } else {
    rect(x + 0.9, y + 2, w - 1.8, 7, ink('fixd'));
    for (let i = 0; i < 2; i += 1) {
      for (let j = 0; j < 2; j += 1) {
        circle(x + w * 0.3 + i * w * 0.4, y + h - 7.5 + j * 4, 0.85, ink('fixd'));
      }
    }
  }
}

kitchenRun(59, 71, 27.75, 5.6, true); // flat 1, along the north wall
kitchenRun(59, 76.6, 5.6, 16, false); // and turning the corner down the west wall
bath(100.25, 162.25, 14.5, 6.6);
basin(100.9, 169.8, 6.4, 4.4);
wc(101.2, 188.6);
bath(230.4, 110, 13.2, 6.2); // flat 2
basin(231, 119, 6, 4.2);
wc(239, 118.6);
bath(230.4, 174.4, 13.2, 6.2); // flat 3
basin(231, 183.4, 6, 4.2);
wc(239, 183);
kitchenRun(178, 71, 22, 5.6, true);
kitchenRun(178, 195.4, 22, 5.6, true);
const FITTINGS = section();

/* ------------------------------------------------------------------ the room names */

/** Room name, its floor area under it. The area is a number and stays a literal. */
const ROOMS: readonly {
  readonly x: number;
  readonly y: number;
  readonly name: Message;
  readonly area: string;
}[] = [
  { x: 73.5, y: 95.5, name: 'plan.room.kitchen', area: '9,8 m²' },
  { x: 109.5, y: 90, name: 'plan.room.bedroom', area: '12,6 m²' },
  { x: 86, y: 133, name: 'plan.room.living', area: '24,6 m²' },
  { x: 78.5, y: 183, name: 'plan.room.bedroom', area: '14,2 m²' },
  { x: 107.5, y: 178.3, name: 'plan.room.bathroom', area: '5,1 m²' },
  { x: 110.6, y: 191.6, name: 'plan.room.wc', area: '1,8 m²' },
  { x: 80, y: 218, name: 'plan.room.loggia', area: '5,4 m²' },
  { x: 154, y: 131, name: 'plan.room.stair', area: '' },
  { x: 154, y: 78.6, name: 'plan.room.lift', area: '' },
  { x: 154, y: 175, name: 'plan.room.hall', area: '' },
  { x: 154, y: 196, name: 'plan.room.lobby', area: '' },
  { x: 192.5, y: 100, name: 'plan.room.living', area: '22,4 m²' },
  { x: 227, y: 90, name: 'plan.room.bedroom', area: '13,8 m²' },
  { x: 227, y: 130, name: 'plan.room.bathroom', area: '4,6 m²' },
  { x: 192.5, y: 167, name: 'plan.room.living', area: '20,8 m²' },
  { x: 227, y: 156, name: 'plan.room.bedroom', area: '12,1 m²' },
  { x: 227, y: 194, name: 'plan.room.bathroom', area: '4,2 m²' },
];
for (const room of ROOMS) {
  label(room.x, room.y, room.name, ink('roomName'));
  if (room.area !== '') glyph(room.x, room.y + 4.4, room.area, ink('roomArea'));
}
label(122.5, 150, 'plan.room.hall', ink('roomName'), true);
// The flat numbers are identifiers — the same string on a Slovak, German or English sheet.
for (const [x, y, text] of [
  [63, 116, 'B 1.1'],
  [182, 86, 'B 1.2'],
  [182, 187, 'B 1.3'],
] as const) {
  glyph(x, y, text, ink('flatNo'));
}
const ROOM_NAMES = section();

/* ------------------------------------------------------------------ the chains */

// Three chains across the top — openings, axis to axis, overall — and two down the left,
// each hung off its own set of witness lines.
for (const x of [53, 62, 80, 98, 120, 182, 202, 218, 238, 251]) line(x, 63, x, 58.6, ink('dimx'));
for (const x of [56, 96, 132, 176, 212, 248]) line(x, 63, x, 50.6, ink('dimx'));
for (const x of [53, 251]) line(x, 63, x, 42.6, ink('dimx'));
chainH(57, [53, 62, 80, 98, 120, 182, 202, 218, 238, 251]);
chainH(49, [56, 96, 132, 176, 212, 248]);
chainH(41, [53, 251]);
for (const y of [65, 76, 94, 112, 132, 138, 156, 168, 190, 207]) line(51, y, 47.6, y, ink('dimx'));
for (const y of [68, 104, 140, 176, 204]) line(51, y, 39.6, y, ink('dimx'));
for (const y of [65, 207]) line(51, y, 31.6, y, ink('dimx'));
chainV(46, [65, 76, 94, 112, 132, 138, 156, 168, 190, 207]);
chainV(38, [68, 104, 140, 176, 204]);
const CHAINS = section();

/* ------------------------------------------------------------------ the two marks */

/**
 * A version cloud: the rectangle walked clockwise with an outward arc per step. This is the
 * one mark on the sheet that is not the building — it is the product, drawn on the drawing.
 */
function cloud(x: number, y: number, w: number, h: number, r = 2.4) {
  const stops: (readonly [number, number])[] = [];
  const nx = Math.max(2, Math.round(w / (r * 1.7)));
  const ny = Math.max(2, Math.round(h / (r * 1.7)));
  for (let i = 0; i < nx; i += 1) stops.push([x + (w * i) / nx, y]);
  for (let i = 0; i < ny; i += 1) stops.push([x + w, y + (h * i) / ny]);
  for (let i = 0; i < nx; i += 1) stops.push([x + w - (w * i) / nx, y + h]);
  for (let i = 0; i < ny; i += 1) stops.push([x, y + h - (h * i) / ny]);
  const head = stops[0];
  if (head === undefined) return;
  let d = `M${String(round(head[0]))},${String(round(head[1]))} `;
  for (let i = 1; i <= stops.length; i += 1) {
    const p = stops[i % stops.length];
    if (p === undefined) continue;
    d += `A${String(r)},${String(r)} 0 0 1 ${String(round(p[0]))},${String(round(p[1]))} `;
  }
  path(d, ink('rev'));
}

cloud(93.5, 59.5, 31, 17);
path('M128.5,54.4 L132.4,61.2 L124.6,61.2 Z', ink('rev'));
glyph(128.5, 60.5, '4', ink('revText'));
circle(196, 150, 3.9, ink('pin'));
glyph(196, 151.3, '3', ink('pinText'));
line(196, 153.9, 196, 159, ink('pinl'));
// Drawn after the pin, because its swing crosses the pin's leader and the leaf wins.
doorH(160, 163, 148, 12, 'left', 'down');
const MARKS = section();

/* ------------------------------------------------------------------ the band */

// North point.
circle(288, 44, 9.6, ink('tbLine'));
path('M288,34.8 L283.4,51 L288,47.4 L292.6,51 Z', ink('north'));
label(288, 31.4, 'north', ink('northText'));

// Scale bar: five 1 m divisions, alternately filled, numbered at every division.
const BAR_X = 306;
const BAR_Y = 40;
for (let i = 0; i < 5; i += 1) {
  rect(BAR_X + i * 10, BAR_Y, 10, 3.2, i % 2 === 0 ? ink('sbFilled') : ink('sbOpen'));
}
for (let i = 0; i < 6; i += 1) glyph(BAR_X + i * 10, BAR_Y + 7.2, String(i), ink('sbText'));
label(BAR_X + 27, BAR_Y - 2.4, 'sheet.scale', ink('sbLabel'));
glyph(BAR_X + 54.5, BAR_Y + 2.6, 'm', ink('sbText'));

// The key. Four entries, and two of them are the product rather than the building.
rect(272, 64, 108, 42, ink('tbLine'));
line(272, 71.6, 380, 71.6, ink('tbLine'));
label(275.5, 69.6, 'plan.key.heading', ink('tbHead'));
type KeySwatch = 'masonry' | 'partition' | 'version' | 'comment';
const KEY_ENTRIES: readonly (readonly [KeySwatch, Message])[] = [
  ['masonry', 'plan.key.masonry'],
  ['partition', 'plan.key.partition'],
  ['version', 'plan.key.version'],
  ['comment', 'plan.key.comment'],
];
let keyY = 75.4;
for (const [swatch, message] of KEY_ENTRIES) {
  if (swatch === 'masonry') {
    rect(275.5, keyY, 8, 5, ink('wallBg'));
    hatchRect(275.5, keyY, 8, 5);
    rect(275.5, keyY, 8, 5, ink('wallO2'));
  } else if (swatch === 'partition') {
    rect(275.5, keyY + 1, 8, 3, ink('part'));
    line(275.5, keyY + 2.5, 283.5, keyY + 2.5, ink('glass'));
  } else if (swatch === 'version') {
    const y = keyY + 2.5;
    path(
      `M275.5,${String(y)} A1.6,1.6 0 0 1 278.7,${String(y)} A1.6,1.6 0 0 1 281.9,${String(y)} ` +
        `A1.6,1.6 0 0 1 283.5,${String(y)}`,
      ink('rev'),
    );
  } else {
    circle(279.5, keyY + 2.5, 2.4, ink('pin'));
  }
  label(287.5, keyY + 4, message, ink('tbBody'));
  keyY += 7.4;
}

// The version table. Four versions, the last of them the one the cloud is drawn around.
rect(272, 110, 108, 38, ink('tbLine'));
line(272, 117.6, 380, 117.6, ink('tbLine'));
for (const x of [286, 344]) line(x, 110, x, 148, ink('tbLine'));
label(275.5, 115.6, 'sheet.rev.column.rev', ink('tbHead'));
label(289.5, 115.6, 'sheet.rev.column.change', ink('tbHead'));
label(347.5, 115.6, 'sheet.tb.date', ink('tbHead'));
const VERSIONS: readonly (readonly [string, Message, string])[] = [
  ['r01', 'plan.rev.1', '04/2026'],
  ['r02', 'plan.rev.2', '06/2026'],
  ['r03', 'plan.rev.3', '07/2026'],
  ['r04', 'plan.rev.4', '09/2026'],
];
let revY = 124.4;
for (const [index, entry] of VERSIONS.entries()) {
  const [id, message, date] = entry;
  if (index > 0) line(272, revY - 4.9, 380, revY - 4.9, ink('tbRule'));
  glyph(275.5, revY, id, ink('tbValueSmall'));
  label(289.5, revY, message, ink('tbBody'));
  glyph(347.5, revY, date, ink('tbValueSmall'));
  revY += 7.5;
}

// The note. Two lines, and the second of them is the reason the product exists.
label(272, 158, 'plan.note.heading', ink('tbHead'));
label(272, 163.6, 'plan.note.dimensions', ink('tbBody'));
label(272, 169.2, 'plan.note.validity', ink('tbBody'));

// The title block. Micro-label above, value below — no colons, because the colon is the one
// piece of punctuation that has to be re-typeset in every language for no gain.
const TB_X = 272;
const TB_Y = 196;
rect(TB_X, TB_Y, 108, 88, ink('tbBox'));
for (const y of [210, 224, 238, 250, 262, 272]) line(TB_X, y, 380, y, ink('tbLine'));
for (const [y0, y1] of [
  [210, 224],
  [250, 262],
  [262, 272],
] as const) {
  line(330, y0, 330, y1, ink('tbLine'));
}
label(TB_X + 3.5, TB_Y + 5.4, 'sheet.tb.project', ink('tbKey'));
label(TB_X + 3.5, TB_Y + 11.6, 'sheet.project', ink('tbValue'));
label(TB_X + 3.5, 215.4, 'sheet.tb.object', ink('tbKey'));
glyph(TB_X + 3.5, 221.6, 'SO 01', ink('tbValue'));
label(333.5, 215.4, 'sheet.tb.stage', ink('tbKey'));
glyph(333.5, 221.6, 'DSP', ink('tbValue'));
label(TB_X + 3.5, 229.4, 'sheet.tb.sheet', ink('tbKey'));
label(TB_X + 3.5, 235.6, 'plan.sheetName', ink('tbValue'));
label(TB_X + 3.5, 243.4, 'sheet.tb.part', ink('tbKey'));
label(TB_X + 3.5, 247.6, 'plan.part', ink('tbBody'));
label(TB_X + 3.5, 255.4, 'sheet.tb.scale', ink('tbKey'));
glyph(TB_X + 3.5, 260.4, '1:100', ink('tbValue'));
label(333.5, 255.4, 'sheet.tb.date', ink('tbKey'));
glyph(333.5, 260.4, '09/2026', ink('tbValue'));
label(TB_X + 3.5, 266.4, 'sheet.tb.number', ink('tbKey'));
glyph(TB_X + 3.5, 271, 'RES-B-L01-A201', ink('tbValue'));
label(333.5, 266.4, 'sheet.tb.version', ink('tbKey'));
glyph(333.5, 271, 'r04', ink('tbValue'));
// The wordmark is a proper noun and never translates; the line beside it says what a
// controlled drawing is, which does.
glyph(TB_X + 3.5, 280.4, 'KALQA', ink('wordmark'));
label(376.5, 280.4, 'sheet.tb.managed', ink('tbFoot'));
const BAND = section();

/* ------------------------------------------------------------------ the schedule */

label(31, 245.4, 'plan.schedule.heading', ink('tbHead'));
rect(31, 248, 168, 34, ink('tbLine'));
line(31, 255.6, 199, 255.6, ink('tbLine'));
for (const x of [56, 104, 150]) line(x, 248, x, 282, ink('tbLine'));
label(34, 253.6, 'plan.schedule.flat', ink('tbHead'));
label(59, 253.6, 'plan.schedule.layout', ink('tbHead'));
label(107, 253.6, 'plan.schedule.area', ink('tbHead'));
label(153, 253.6, 'plan.schedule.aspect', ink('tbHead'));
const SCHEDULE: readonly (readonly [string, Message, string, Message])[] = [
  ['B 1.1', 'plan.schedule.type3', '78,4 m²', 'plan.schedule.aspect.ws'],
  ['B 1.2', 'plan.schedule.type2', '54,2 m²', 'plan.schedule.aspect.ne'],
  ['B 1.3', 'plan.schedule.type2', '51,6 m²', 'plan.schedule.aspect.es'],
];
let schedY = 262.4;
for (const [index, row] of SCHEDULE.entries()) {
  const [id, layout, area, aspect] = row;
  if (index > 0) line(31, schedY - 4.9, 199, schedY - 4.9, ink('tbRule'));
  glyph(34, schedY, id, ink('tbValueSmall'));
  label(59, schedY, layout, ink('tbBody'));
  glyph(107, schedY, area, ink('tbValueSmall'));
  label(153, schedY, aspect, ink('tbBody'));
  schedY += 8.6;
}
const SCHEDULE_BLOCK = section();

/* ------------------------------------------------------------------ the component */

const SECTIONS: readonly (readonly Shape[])[] = [
  BEHIND,
  SHEET,
  GRID,
  WALLS,
  WINDOWS,
  DOORS,
  CORE_EQUIPMENT,
  FITTINGS,
  ROOM_NAMES,
  CHAINS,
  MARKS,
  BAND,
  SCHEDULE_BLOCK,
];

/** Two decimals is the precision the geometry was authored at; more is noise in the markup. */
function round(n: number) {
  return Number(n.toFixed(2));
}

export interface PlanSheetDrawingProps {
  /**
   * How the sheet meets a frame that is not 4:3. `cover` fills it and crops the margins;
   * `contain` fits the whole sheet inside it. The title block and the schedule live at the
   * edges, so a square plate asks for `contain`.
   */
  readonly fit?: 'cover' | 'contain';
  /**
   * What the sheet is lying on, which decides how its edge is drawn. On `paper` the plate is
   * white and the sheet is separated from it by a soft drop shadow. On `ink` that shadow is a
   * dark blur over a dark surface — invisible work — so the shadow is dropped and the sheet
   * keeps the hairline trim edge instead, which is all the separation white paper on ink
   * needs.
   */
  readonly ground?: 'paper' | 'ink';
}

function draw(
  shape: Shape,
  key: number,
  scope: string,
  ground: 'paper' | 'ink',
  t: (m: Message) => string,
) {
  switch (shape.kind) {
    case 'rect':
      return (
        <rect
          key={key}
          className={shape.cls}
          x={round(shape.x)}
          y={round(shape.y)}
          width={round(shape.w)}
          height={round(shape.h)}
          rx={shape.rx > 0 ? shape.rx : undefined}
          fill={shape.hatched ? `url(#${scope}-hatch)` : undefined}
        />
      );
    case 'sheet':
      return (
        <rect
          key={key}
          className={ground === 'ink' ? styles.sheetInk : styles.sheetPaper}
          x={shape.x}
          y={shape.y}
          width={shape.w}
          height={shape.h}
          filter={ground === 'ink' ? undefined : `url(#${scope}-shadow)`}
        />
      );
    case 'line':
      return (
        <line
          key={key}
          className={shape.cls}
          x1={round(shape.x1)}
          y1={round(shape.y1)}
          x2={round(shape.x2)}
          y2={round(shape.y2)}
        />
      );
    case 'path':
      return (
        <path
          key={key}
          className={shape.cls}
          d={shape.d}
          fillRule={shape.evenOdd ? 'evenodd' : undefined}
          fill={shape.hatched ? `url(#${scope}-hatch)` : undefined}
        />
      );
    case 'circle':
      return (
        <circle
          key={key}
          className={shape.cls}
          cx={round(shape.cx)}
          cy={round(shape.cy)}
          r={shape.r}
        />
      );
    case 'ellipse':
      return (
        <ellipse
          key={key}
          className={shape.cls}
          cx={round(shape.cx)}
          cy={round(shape.cy)}
          rx={shape.rx}
          ry={shape.ry}
        />
      );
    case 'glyph':
    case 'label': {
      const x = round(shape.x);
      const y = round(shape.y);
      return (
        <text
          key={key}
          className={shape.cls}
          x={x}
          y={y}
          transform={shape.rotate ? `rotate(-90 ${String(x)} ${String(y)})` : undefined}
        >
          {shape.kind === 'glyph' ? shape.text : t(shape.message)}
        </text>
      );
    }
  }
}

export function PlanSheetDrawing({ fit = 'cover', ground = 'paper' }: PlanSheetDrawingProps) {
  const t = useMessages('drawing');
  /* Per-instance ids for the hatch pattern and the sheet's drop shadow. A page drawing this
     twice — and the landing and the entrance are one route apart — would otherwise have two
     elements sharing an `id`, which is invalid markup whose `url(#…)` references all resolve
     to whichever copy the parser saw first. The colons React puts in `useId` are stripped: a
     fragment identifier carrying them is a URL reference nobody should have to reason about. */
  const scope = `kalqa-plan-${useId().replace(/:/g, '')}`;

  return (
    <svg
      className={styles.view}
      viewBox="0 0 400 300"
      preserveAspectRatio={fit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice'}
      aria-hidden
    >
      <defs>
        <pattern
          id={`${scope}-hatch`}
          width="3.2"
          height="3.2"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line className={styles.hatchline} x1="0" y1="0" x2="0" y2="3.2" />
        </pattern>
        <filter id={`${scope}-shadow`} x="-10%" y="-10%" width="130%" height="130%">
          <feDropShadow className={styles.shadow} dx="1.2" dy="1.6" stdDeviation="1.6" />
        </filter>
      </defs>
      {SECTIONS.map((shapes, group) => (
        <g key={group}>{shapes.map((child, index) => draw(child, index, scope, ground, t))}</g>
      ))}
    </svg>
  );
}
